// Google Analytics 4 & Meta Pixel Utility Functions

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: {
        page_path?: string
        page_title?: string
        page_location?: string
        [key: string]: any
      }
    ) => void
    dataLayer: any[]
    fbq: (
      command: 'track' | 'trackCustom' | 'init',
      eventName: string,
      parameters?: {
        [key: string]: any
      }
    ) => void
  }
}

// Initialize GA4
export const initGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-35C7FLN5FV', {
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
      page_location: window.location.href,
    })
  }
}

// Track page view
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-35C7FLN5FV', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.origin + path,
    })
  }
}

// Track custom events
export const trackEvent = (
  eventName: string,
  eventParams?: {
    event_category?: string
    event_label?: string
    value?: number
    [key: string]: any
  }
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
    })
  }
}

// Track button clicks
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('button_click', {
    event_category: 'engagement',
    event_label: buttonName,
    button_name: buttonName,
    button_location: location || window.location.pathname,
  })
}

// Track form submissions
export const trackFormSubmit = (formName: string, formLocation?: string) => {
  trackEvent('form_submit', {
    event_category: 'engagement',
    event_label: formName,
    form_name: formName,
    form_location: formLocation || window.location.pathname,
  })
}

// Track outbound links
export const trackOutboundLink = (url: string, linkText?: string) => {
  trackEvent('outbound_link_click', {
    event_category: 'outbound',
    event_label: linkText || url,
    link_url: url,
    link_text: linkText,
  })
}

// Track file downloads
export const trackDownload = (fileName: string, fileType?: string) => {
  trackEvent('file_download', {
    event_category: 'downloads',
    event_label: fileName,
    file_name: fileName,
    file_type: fileType,
  })
}

// Track video plays
export const trackVideoPlay = (videoName: string, videoLocation?: string) => {
  trackEvent('video_play', {
    event_category: 'video',
    event_label: videoName,
    video_name: videoName,
    video_location: videoLocation || window.location.pathname,
  })
}

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll', {
    event_category: 'engagement',
    event_label: `${depth}%`,
    scroll_depth: depth,
  })
}

// Track search queries
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', {
    event_category: 'site_search',
    event_label: searchTerm,
    search_term: searchTerm,
    results_count: resultsCount,
  })
}

// Track service page views
export const trackServiceView = (serviceName: string) => {
  trackEvent('service_view', {
    event_category: 'services',
    event_label: serviceName,
    service_name: serviceName,
  })
}

// Track blog post views
export const trackBlogView = (postTitle: string, postSlug: string) => {
  trackEvent('blog_view', {
    event_category: 'blog',
    event_label: postTitle,
    post_title: postTitle,
    post_slug: postSlug,
  })
}

// Track contact form submissions
export const trackContactForm = (formType: string) => {
  trackEvent('contact_form_submit', {
    event_category: 'contact',
    event_label: formType,
    form_type: formType,
  })
}

// Track Cal.com meeting scheduling
export const trackMeetingSchedule = () => {
  trackEvent('meeting_scheduled', {
    event_category: 'engagement',
    event_label: 'Cal.com Meeting',
  })
}

// Track callback request
export const trackCallbackRequest = () => {
  trackEvent('callback_request', {
    event_category: 'contact',
    event_label: 'Callback Request',
  })
}

// Track WhatsApp support clicks
export const trackWhatsAppClick = (location?: string) => {
  trackEvent('whatsapp_click', {
    event_category: 'support',
    event_label: 'WhatsApp Support',
    click_location: location || window.location.pathname,
  })
}

// ==================== META PIXEL FUNCTIONS ====================

// Helper function to track Meta Pixel events
const trackMetaPixelEvent = (
  eventName: string,
  parameters?: {
    [key: string]: any
  }
) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters)
  }
}

// Track custom Meta Pixel events
const trackCustomMetaEvent = (
  eventName: string,
  parameters?: {
    [key: string]: any
  }
) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, parameters)
  }
}

// Track page view (Meta Pixel)
export const trackMetaPageView = () => {
  trackMetaPixelEvent('PageView')
}

// Track content view
export const trackMetaViewContent = (params?: {
  content_name?: string
  content_category?: string
  content_ids?: string[]
  content_type?: string
  value?: number
  currency?: string
}) => {
  trackMetaPixelEvent('ViewContent', {
    content_name: params?.content_name,
    content_category: params?.content_category,
    content_ids: params?.content_ids || [],
    content_type: params?.content_type,
    value: params?.value,
    currency: params?.currency || 'TRY',
  })
}

// Track add to cart
export const trackMetaAddToCart = (params: {
  content_name: string
  content_ids: string[]
  content_type: string
  value: number
  currency?: string
}) => {
  trackMetaPixelEvent('AddToCart', {
    content_name: params.content_name,
    content_ids: params.content_ids,
    content_type: params.content_type,
    value: params.value,
    currency: params.currency || 'TRY',
  })
}

// Track initiate checkout
export const trackMetaInitiateCheckout = (params: {
  content_ids: string[]
  content_category?: string
  num_items: number
  value: number
  currency?: string
}) => {
  trackMetaPixelEvent('InitiateCheckout', {
    content_ids: params.content_ids,
    content_category: params.content_category,
    num_items: params.num_items,
    value: params.value,
    currency: params.currency || 'TRY',
  })
}

// Track purchase
export const trackMetaPurchase = (params: {
  content_ids: string[]
  content_name: string
  content_type: string
  value: number
  currency?: string
  num_items?: number
}) => {
  trackMetaPixelEvent('Purchase', {
    content_ids: params.content_ids,
    content_name: params.content_name,
    content_type: params.content_type,
    value: params.value,
    currency: params.currency || 'TRY',
    num_items: params.num_items || 1,
  })
}

// Track lead generation
export const trackMetaLead = (params?: {
  content_name?: string
  content_category?: string
  value?: number
  currency?: string
}) => {
  trackMetaPixelEvent('Lead', {
    content_name: params?.content_name,
    content_category: params?.content_category,
    value: params?.value,
    currency: params?.currency || 'TRY',
  })
}

// Track contact form submission
export const trackMetaContact = (formType: string) => {
  trackMetaPixelEvent('Contact', {
    content_name: formType,
  })
}

// Track search
export const trackMetaSearch = (searchString: string) => {
  trackMetaPixelEvent('Search', {
    search_string: searchString,
  })
}

// Track custom events for specific actions
export const trackMetaCustomEvent = (eventName: string, params?: any) => {
  trackCustomMetaEvent(eventName, params)
}

// Combined tracking functions (GA4 + Meta Pixel)
export const trackServiceViewCombined = (serviceName: string) => {
  // Track in GA4
  trackServiceView(serviceName)
  
  // Track in Meta Pixel
  trackMetaViewContent({
    content_name: serviceName,
    content_category: 'service',
    content_type: 'service',
  })
}

export const trackBlogViewCombined = (postTitle: string, postSlug: string) => {
  // Track in GA4
  trackBlogView(postTitle, postSlug)
  
  // Track in Meta Pixel
  trackMetaViewContent({
    content_name: postTitle,
    content_category: 'blog',
    content_type: 'blog_post',
    content_ids: [postSlug],
  })
}

export const trackContactFormCombined = (formType: string) => {
  // Track in GA4
  trackContactForm(formType)
  
  // Track in Meta Pixel
  trackMetaLead({
    content_name: formType,
    content_category: 'contact_form',
  })
}

export const trackWhatsAppClickCombined = (location?: string) => {
  // Track in GA4
  trackWhatsAppClick(location)
  
  // Track in Meta Pixel
  trackMetaCustomEvent('WhatsAppClick', {
    location: location || window.location.pathname,
  })
}

export const trackSearchCombined = (searchTerm: string, resultsCount?: number) => {
  // Track in GA4
  trackSearch(searchTerm, resultsCount)
  
  // Track in Meta Pixel
  trackMetaSearch(searchTerm)
}





